import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { Component, h, Method, Prop, State, Watch } from "@stencil/core"
import { HorizontalMarkSizeEnum, HorizontalMarkSizeEnumsObj } from "../../enums/HorizontalMarkSizeEnum"
import { PaymentMethodEnum, PaymentMethodEnumsObj } from "../../enums/PaymentMethodEnum"
import { srcScreenError } from "../../helpers/CommonRenders"
import { validatePaymentMethodEnum } from "../../helpers/Validators"
import { getLanguageHandler, getLocale, getSupportedLocaleData } from "../../languageHandler/LanguageHandler"
import { CardValidationModel } from "../../models/CardValidationModel"
import { CardHolderInfo } from "../commonCardInput/CardHolderInfo"
import { CardScreenMessageController } from "../commonCardInput/CardScreenMessageController"
import { CardScreenStateModel } from "../commonCardInput/CardScreenStateModel"
import { ConsumerInfoFormConfig } from "../consumerInfoForm/ConsumerInfoFormConfig.model"
import { ConsumerInfoFormError, ConsumerInfoFormValidity } from "../consumerInfoForm/ConsumerInfoFormValidity.model"
import { ConsumerInfoValue, createEmptyConsumerInfoValue } from "../consumerInfoForm/ConsumerInfoValue.model"
import { InfoMessageModel, MessageMaxLength } from "../infoMessage/InfoMessageModel"

const DEFAULT_UNEXPECTED_ERROR = "Something went wrong. Please try again."
const NEW_USER_PAYMENT_METHODS: PaymentMethodEnum[] = [
  PaymentMethodEnumsObj.MC,
  PaymentMethodEnumsObj.VISA,
  PaymentMethodEnumsObj.MC_VISA,
]

type TwoStepCardFlowElement = HTMLTwoStepCardFlowElement & {
  returnToCardStep: () => Promise<void>
  scrollToTop: () => Promise<boolean>
}

/** Entire screen for the First-Time validate-first and encrypt-later card enrollment flow. */
@Component({
  tag: "new-user-screen",
  styleUrl: "../commonCardInput/CardInputScreen.scss",
})
export class NewUserScreen {
  @Prop() locale?: string
  /** This flow currently supports `master`, `visa`, or `master,visa`. */
  @Prop() paymentMethod: PaymentMethodEnum
  @Prop() infoMessage?: InfoMessageModel
  @Prop() cardHolderInfo?: CardHolderInfo
  @Prop() consumerInfoValue: ConsumerInfoValue = createEmptyConsumerInfoValue()
  @Prop() consumerInfoConfig?: ConsumerInfoFormConfig
  @Prop() consumerInfoErrors: ConsumerInfoFormError[] = []
  @Prop() consumerInfoOnChange?: (value: ConsumerInfoValue) => void
  @Prop() consumerInfoValidityOnChange?: (validity: ConsumerInfoFormValidity) => void
  @Prop() isLoading?: boolean
  @Prop() env: "QA" | "PROD"
  @Prop() psp: string
  @Prop() amount?: string
  @Prop() currency?: string
  @Prop() closeButtonOnClick?: () => void
  @Prop() changeUserButtonOnClick?: () => void
  @Prop() cardValidationOnChange?: (validation: CardValidationModel) => boolean | Promise<boolean>
  @Prop() invalidCardOnChange?: (validation: CardValidationModel) => void
  @Prop() prepareStoreAndPay?: (cardBrand: "MASTERCARD" | "VISA") => boolean | Promise<boolean>
  @Prop() getStartedButtonOnClick?: (
    cardScreenState: CardScreenStateModel,
    rememberMe?: boolean
  ) => void | Promise<void>

  @State() isRememberMeChecked = false
  @State() displayedInfoMessage?: InfoMessageModel

  private languageHandler: LanguageHandler
  private supportedLocale: string
  private horizontalMarkSize: HorizontalMarkSizeEnum
  private errorMessage: string[] = []
  private pendingProcessingErrorScroll = false
  private twoStepCardFlow?: TwoStepCardFlowElement
  private messageController = new CardScreenMessageController(message => (this.displayedInfoMessage = message))

  componentWillLoad() {
    if (!this.paymentMethod) {
      this.errorMessage.push("'paymentMethod' prop is not defined")
    } else if (!validatePaymentMethodEnum(this.paymentMethod)) {
      this.errorMessage.push("'paymentMethod' prop is not valid")
    } else if (!NEW_USER_PAYMENT_METHODS.includes(this.paymentMethod)) {
      this.errorMessage.push("'paymentMethod' prop is not supported by this flow")
    }
    if (this.env !== "PROD" && this.env !== "QA") {
      this.errorMessage.push("'env' param must be 'QA' or 'PROD'")
    }
    if (typeof this.psp !== "string" || !this.psp.trim()) {
      this.errorMessage.push("'psp' param must be a non-empty string")
    }
    if (this.errorMessage.length > 0) {
      return
    }

    const localeData = getSupportedLocaleData(this.locale)
    this.supportedLocale = getLocale(localeData)
    this.languageHandler = getLanguageHandler(localeData.lang)
    this.horizontalMarkSize = HorizontalMarkSizeEnumsObj.S16

    const initialInfoMessage =
      this.infoMessage && this.infoMessage.infoMessage.length > MessageMaxLength.NEW_USER_SCREEN
        ? {
            ...this.infoMessage,
            infoMessage: this.infoMessage.infoMessage.substring(0, MessageMaxLength.NEW_USER_SCREEN),
          }
        : this.infoMessage
    this.messageController.setExternalMessage(initialInfoMessage)
  }

  @Watch("infoMessage")
  infoMessageChanged(message?: InfoMessageModel) {
    if (message?.infoType === "ERROR" && message.infoMessage) {
      this.pendingProcessingErrorScroll = true
    }
    this.messageController.setExternalMessage(message)
  }

  private showUnexpectedAttemptError = () => {
    this.pendingProcessingErrorScroll = true
    this.messageController.showLocalError(
      this.languageHandler.getString("unexpectedError") || DEFAULT_UNEXPECTED_ERROR
    )
  }

  private clearRecoverableActionError = () => {
    this.messageController.clearActionError()
  }

  componentDidRender() {
    if (!this.pendingProcessingErrorScroll) return

    this.pendingProcessingErrorScroll = false
    void this.twoStepCardFlow?.scrollToTop()
  }

  /** Returns the flow to the card-entry step after a recoverable card error. */
  @Method()
  async returnToCardStep(): Promise<void> {
    await this.twoStepCardFlow?.returnToCardStep()
  }

  private handleCardValidation = async (validation: CardValidationModel): Promise<boolean> => {
    this.messageController.beginAttempt()
    return (await this.cardValidationOnChange?.(validation)) !== false
  }

  private handleInvalidCard = (validation: CardValidationModel) => {
    this.messageController.beginAttempt()
    this.invalidCardOnChange?.(validation)
  }

  private handlePrepareStoreAndPay = async (cardBrand: "MASTERCARD" | "VISA"): Promise<boolean> => {
    this.messageController.beginAttempt()
    return (await this.prepareStoreAndPay?.(cardBrand)) !== false
  }

  private renderCardStepHeader() {
    return (
      <div slot="card-step-header">
        <div class="greetingText">{this.languageHandler.getString("welcomeToClickToPay")}</div>
        <div class="cardInputDescriptionText">{this.languageHandler.getString("fastSecureOnlineCheckout")}</div>
        <div class="horizontalMarkContainer">
          <span class="horizontalMarkText">{this.languageHandler.getString("weAccept")}</span>
          <horizontal-mark
            locale={this.supportedLocale}
            paymentMethod={this.paymentMethod}
            horizontalMarkSize={this.horizontalMarkSize}
          />
        </div>
        <div class="switchUserComponent">
          <switch-user
            locale={this.supportedLocale}
            userCredentials={this.languageHandler.getString("cardSwitchUser")}
            switchUserText={this.languageHandler.getString("signIn")}
            changeUserButtonOnClick={this.changeUserButtonOnClick}
          />
        </div>
      </div>
    )
  }

  render() {
    if (this.errorMessage.length > 0) {
      return srcScreenError("New Card Screen", this.errorMessage)
    }

    return (
      <div class="cardScreen consumerCardScreen">
        <div class="closeButtonContainer">
          {typeof this.closeButtonOnClick === "function" && (
            <close-button closeButtonOnClick={this.closeButtonOnClick} />
          )}
        </div>
        <div class="cardScreenContent">
          <div class="consumerCardScrollableContent">
            <two-step-card-flow
              ref={element => (this.twoStepCardFlow = element as TwoStepCardFlowElement)}
              locale={this.supportedLocale}
              paymentMethod={this.paymentMethod}
              env={this.env}
              psp={this.psp}
              cardHolderInfo={this.cardHolderInfo}
              consumerInfoValue={this.consumerInfoValue}
              consumerInfoConfig={this.consumerInfoConfig}
              consumerInfoErrors={this.consumerInfoErrors}
              showRememberMe={true}
              rememberMe={this.isRememberMeChecked}
              isLoading={this.isLoading === true}
              infoMessage={this.displayedInfoMessage}
              consumerInfoOnChange={this.consumerInfoOnChange}
              consumerInfoValidityOnChange={this.consumerInfoValidityOnChange}
              rememberMeOnChange={rememberMe => (this.isRememberMeChecked = rememberMe)}
              cardValidationOnChange={this.handleCardValidation}
              invalidCardOnChange={this.handleInvalidCard}
              prepareStoreAndPay={this.handlePrepareStoreAndPay}
              unexpectedErrorOnChange={this.showUnexpectedAttemptError}
              goBackOnChange={this.clearRecoverableActionError}
              encryptedCardOnChange={(cardState, rememberMe) =>
                this.getStartedButtonOnClick?.(cardState, rememberMe)
              }>
              {this.renderCardStepHeader()}
            </two-step-card-flow>
          </div>
        </div>
      </div>
    )
  }
}
